import { resolveMediaUrl } from '@/utils/resolve-media-url';
import { enrichMarketDetailNavigation } from '@/utils/market-detail-map';
import { resolveChannelMarketDetailRoute } from '@/utils/channel-market-detail-route.js';

export const TYPE_TO_ICON = {
  GENERAL: 'bell',
  FOLLOW: 'follow',
  MESSAGE: 'chat',
  MENTION: 'mention',
};

export const API_TYPE_TO_TAB = {
  GENERAL: 'general',
  FOLLOW: 'following',
  MESSAGE: 'messages',
  MENTION: 'mentions',
};

export function formatRelativeTime(isoString, locale = 'en') {
  if (!isoString) return '';
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return rtf.format(-diffMin, 'minute');
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return rtf.format(-diffHour, 'hour');
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return rtf.format(-diffDay, 'day');
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return rtf.format(-diffMonth, 'month');
  return rtf.format(-Math.floor(diffMonth / 12), 'year');
}

export function normalizeUnreadCounts(data) {
  if (!data || typeof data !== 'object') {
    return { total: 0, GENERAL: 0, FOLLOW: 0, MESSAGE: 0, MENTION: 0 };
  }
  return {
    total: Number(data.totalCount ?? 0),
    GENERAL: Number(data.generalCount ?? 0),
    FOLLOW: Number(data.followCount ?? 0),
    MESSAGE: Number(data.messageCount ?? 0),
    MENTION: Number(data.mentionCount ?? 0),
  };
}

export function normalizeNotificationPage(data) {
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data)) return data;
  return [];
}

function parseMarketIdFromTargetUrl(targetUrl) {
  const raw = String(targetUrl ?? '').trim();
  if (!raw.includes('/market/')) return '';
  try {
    const url = new URL(raw.startsWith('/') ? raw : `/${raw}`, 'http://local');
    const match = url.pathname.match(/^\/market\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    const part = raw.split('/market/').pop() || '';
    return part.split(/[?#]/)[0] || '';
  }
}

/**
 * Prefer API collId/marketId/slug; never treat bare targetId as market id
 * (legacy fill notifs stored order_id there).
 */
export function mapNotificationRow(row, locale = 'en') {
  const type = row.type || 'GENERAL';
  const targetUrl = String(row.targetUrl || '').trim();
  const targetId = String(row.targetId || '').trim();
  const collId = String(row.collId ?? '').trim();
  const slug = String(row.slug ?? '').trim();
  const marketId = String(row.marketId ?? '').trim() || parseMarketIdFromTargetUrl(targetUrl);

  return {
    id: String(row.id ?? row.notificationId ?? '').trim(),
    title: row.title || row.senderNickName || '—',
    content: row.content || '',
    unread: row.isRead === false || row.isRead === 0,
    iconType: TYPE_TO_ICON[type] || 'bell',
    senderAvatar: resolveMediaUrl(row.senderAvatar || ''),
    timeIso: row.createTime || '',
    timeLabel: formatRelativeTime(row.createTime, locale),
    targetUrl,
    targetId,
    collId,
    slug,
    category: String(row.category ?? row.categoryName ?? '').trim(),
    marketId,
    noNavigate: !targetUrl && !collId && !marketId,
    raw: row,
  };
}

/**
 * Build vue-router location from catalog nav fields (collId / marketId / slug).
 * Shared by notification deep-links and account my-comments「关于」.
 * Interval up/down → /crypto/updown/{collId}; sport matchup → /sport/live/{collId};
 * else MarketDetail resolve.
 * @returns {import('vue-router').RouteLocationRaw | string | null}
 */
export function buildCatalogMarketNavigateTarget(item) {
  if (!item || item.noNavigate) return null;

  const collId = String(item.collId ?? '').trim();
  const marketId = String(item.marketId ?? '').trim();
  const slug = String(item.slug ?? '').trim();
  const category = String(item.category ?? item.categoryName ?? '').trim();

  if (collId || marketId) {
    return resolveChannelMarketDetailRoute({
      collId,
      marketId,
      slug,
      category,
      homeTeamName: item.homeTeamName ?? item.home_team_name ?? '',
      awayTeamName: item.awayTeamName ?? item.away_team_name ?? '',
      contestId: item.contestId ?? item.contest_id ?? '',
      markets: item.markets,
      historical: true,
    });
  }

  const targetUrl = String(item.targetUrl ?? '').trim();
  if (!targetUrl) return null;
  if (/^https?:\/\//i.test(targetUrl)) return targetUrl;
  return enrichMarketDetailNavigation(targetUrl);
}

/** @deprecated Prefer buildCatalogMarketNavigateTarget; kept for existing notification callers. */
export function buildNotificationNavigateTarget(item) {
  return buildCatalogMarketNavigateTarget(item);
}

export function panelIconClass(iconType) {
  const map = { bell: 'market', follow: 'follow', chat: 'comment', mention: 'mention' };
  return map[iconType] || 'market';
}
